import React, { useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  LinearProgress,
  Menu,
} from "@mui/material";
import { logout } from "../../reduxStore/actions/auth";
import { axiosConfig } from "../../axios/axiosConfig";
import RandomUser from "../../assets/icons/male-user.png";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import DownloadIcon from "@mui/icons-material/Download";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from "recharts";

// ── Static demo data ─────────────────────────────────────────────────────────

const SPEND_COLORS = [
  "#4285F4",
  "#F59E0B",
  "#016551",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
];

const STAT_META = {
  "Total Clients": {
    icon: <GroupIcon sx={{ color: "#fff", fontSize: 22 }} />,
    iconBg: "#7C3AED",
    route: "/investor/total-clients",
  },
  "New Clients (Today)": {
    icon: <PersonAddIcon sx={{ color: "#fff", fontSize: 22 }} />,
    iconBg: "#F97316",
    route: "/investor/new-clients-today",
  },
  "New Subscribers": {
    icon: <PersonAddIcon sx={{ color: "#fff", fontSize: 22 }} />,
    iconBg: "#016551",
    route: "/investor/new-clients-today",
  },
  "Invested Funds": {
    icon: <AccountBalanceWalletIcon sx={{ color: "#fff", fontSize: 22 }} />,
    iconBg: "#F59E0B",
    route: null,
  },
  "Total Subscribers": {
    icon: <SubscriptionsIcon sx={{ color: "#fff", fontSize: 22 }} />,
    iconBg: "#0EA5E9",
    route: "/investor/total-subscribers",
  },
  "App Downloads": {
    icon: <DownloadIcon sx={{ color: "#fff", fontSize: 22 }} />,
    iconBg: "#EC4899",
    route: null,
  },
  "Active Users": {
    icon: <PeopleAltIcon sx={{ color: "#fff", fontSize: 22 }} />,
    iconBg: "#10B981",
    route: "/investor/active-users",
  },
  "Active Users": {
    icon: <TrendingUpIcon sx={{ color: "#fff", fontSize: 22 }} />,
    iconBg: "#89B443",
    route: "/investor/active-users-yesterday",
  },
  "Total Active Users": {
    icon: <PeopleAltIcon sx={{ color: "#fff", fontSize: 22 }} />,
    iconBg: "#10B981",
    route: "/investor/active-users",
  },
  "Active Subscribers": {
    icon: <SubscriptionsIcon sx={{ color: "#fff", fontSize: 22 }} />,
    iconBg: "#17A96A",
    route: "/investor/active-subscribers",
  },
};

const PIE_COLORS = ["#016551", "#e2e8f0"];

// ── Subcomponents ────────────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#64748b", mb: 0.5 }}>
    {children}
  </Typography>
);

// ── Main Component ────────────────────────────────────────────────────────────
const InvestorDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userState = useSelector((store) => store.auth);
  const userDetails = userState?.user?.userDetails;
  const [subPeriod, setSubPeriod] = useState("7");
  const [downloadPeriod, setDownloadPeriod] = useState("7");
  const [spendPeriod, setSpendPeriod] = useState("30");
  const [spendChartData, setSpendChartData] = useState([]);
  const [totalSpendDisplay, setTotalSpendDisplay] = useState("₹0");
  const [spendLoading, setSpendLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [statCards, setStatCards] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [trendData, setTrendData] = useState([]);
  const [trendLoading, setTrendLoading] = useState(true);
  const [newClientData, setNewClientData] = useState([]);
  const [newClientLoading, setNewClientLoading] = useState(true);
  const [activeSubscribers, setActiveSubscribers] = useState([]);
  const [subscribersLoading, setSubscribersLoading] = useState(true);
  const [subRateFilter, setSubRateFilter] = useState("all");
  const [subRateData, setSubRateData] = useState(null);
  const [subRateLoading, setSubRateLoading] = useState(true);
  const [userBeforeFilter, setUserBeforeFilter] = useState("today");
  const [userBeforeData, setUserBeforeData] = useState(null);
  const [userBeforeLoading, setUserBeforeLoading] = useState(true);
  const [activeSubBeforeFilter, setActiveSubBeforeFilter] = useState("all");
  const [activeSubBeforeData, setActiveSubBeforeData] = useState(null);
  const [activeSubBeforeLoading, setActiveSubBeforeLoading] = useState(true);
  const [subPaymentFilter, setSubPaymentFilter] = useState("today");
  const [subPaymentData, setSubPaymentData] = useState(null);
  const [subPaymentLoading, setSubPaymentLoading] = useState(true);
  const [totalClientsFilter, setTotalClientsFilter] = useState("all");
  const [totalClientsCount, setTotalClientsCount] = useState(null);
  const [totalClientsLoading, setTotalClientsLoading] = useState(true);
  const [newSubsFilter, setNewSubsFilter] = useState("today");
  const [newSubsCount, setNewSubsCount] = useState(null);
  const [newSubsLoading, setNewSubsLoading] = useState(true);
  const [activeUsersYestFilter, setActiveUsersYestFilter] = useState("all");
  const [activeUsersYestCount, setActiveUsersYestCount] = useState(null);
  const [activeUsersYestLoading, setActiveUsersYestLoading] = useState(true);
  useEffect(() => {
    const fetchTrend = async () => {
      try {
        const res = await axiosConfig({
          method: "get",
          url: "/investors/login-subscriber",
        });
        const result = res?.data?.result || [];
        setTrendData(result);
      } catch (e) {
        console.error("trend data error:", e);
      } finally {
        setTrendLoading(false);
      }
    };
    fetchTrend();
  }, []);

  useEffect(() => {
    const fetchDigitalSpend = async () => {
      setSpendLoading(true);
      try {
        const res = await axiosConfig({
          method: "get",
          url: `/investors/digital-spend-chart/?range=${spendPeriod}`,
        });
        setSpendChartData(res.data?.chart_data || []);
        setTotalSpendDisplay(res.data?.total_spend_display || "₹0");
      } catch (e) {
        console.error("digital spend error:", e);
      } finally {
        setSpendLoading(false);
      }
    };
    fetchDigitalSpend();
  }, [spendPeriod]);

  useEffect(() => {
    const fetchRegistration = async () => {
      try {
        const res = await axiosConfig({
          method: "get",
          url: "/investors/registration-trend/",
        });
        const raw = res?.data?.new_registered_users || [];
        const mapped = raw.map(({ date, count }) => {
          const d = new Date(date);
          const label = d.toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
          });
          return { date: label, clients: count };
        });
        setNewClientData(mapped);
      } catch (e) {
        console.error("registration trend error:", e);
      } finally {
        setNewClientLoading(false);
      }
    };
    fetchRegistration();
  }, []);

  useEffect(() => {
    const fetchSubscribers = async () => {
      try {
        const res = await axiosConfig({
          method: "get",
          url: "/investors/today-new-subscriber/",
        });
        setActiveSubscribers(res?.data?.new_subscribers_today || []);
      } catch (e) {
        console.error("subscribers error:", e);
      } finally {
        setSubscribersLoading(false);
      }
    };
    fetchSubscribers();
  }, []);

  useEffect(() => {
    const fetchSubRate = async () => {
      try {
        setSubRateLoading(true);
        const res = await axiosConfig({
          method: "get",
          url: `/investors/subscription-rate/?requred_data=${subRateFilter}`,
        });
        setSubRateData(res?.data || null);
      } catch (e) {
        console.error("subscription rate error:", e);
      } finally {
        setSubRateLoading(false);
      }
    };
    fetchSubRate();
  }, [subRateFilter]);

  useEffect(() => {
    const fetchUserBefore = async () => {
      try {
        setUserBeforeLoading(true);
        const res = await axiosConfig({
          method: "get",
          url: `investors/active-subscriber-data/?filter_type=${userBeforeFilter}`,
        });
        setUserBeforeData(res?.data || null);
      } catch (e) {
        console.error("user before days error:", e);
      } finally {
        setUserBeforeLoading(false);
      }
    };
    fetchUserBefore();
  }, [userBeforeFilter]);

  useEffect(() => {
    const fetchActiveSubBefore = async () => {
      try {
        setActiveSubBeforeLoading(true);
        const res = await axiosConfig({
          method: "get",
          url: activeSubBeforeFilter === "all" ? `/investors/active-subscribers-before-days/` : `/investors/active-subscribers-before-days/?filter_type=${activeSubBeforeFilter}`,
        });
        setActiveSubBeforeData(res?.data || null);
      } catch (e) {
        console.error("active subscribers before days error:", e);
      } finally {
        setActiveSubBeforeLoading(false);
      }
    };
    fetchActiveSubBefore();
  }, [activeSubBeforeFilter]);

  useEffect(() => {
    const fetchSubPayment = async () => {
      try {
        setSubPaymentLoading(true);
        const res = await axiosConfig({
          method: "get",
          url: subPaymentFilter === "all" ? `/investors/subscription-payment-data/` : `/investors/subscription-payment-data/?filter_type=${subPaymentFilter}`,
        });
        setSubPaymentData(res?.data || null);
      } catch (e) {
        console.error("subscription payment data error:", e);
      } finally {
        setSubPaymentLoading(false);
      }
    };
    fetchSubPayment();
  }, [subPaymentFilter]);

  useEffect(() => {
    const fetchTotalClients = async () => {
      setTotalClientsLoading(true);
      try {
        const res = await axiosConfig({
          method: "get",
          url: totalClientsFilter === "all" ? `investors/dashboard/total-clients/` : `investors/dashboard/total-clients/?filter_type=${totalClientsFilter}`,
        });
        setTotalClientsCount(res.data?.value ?? null);
      } catch (e) {
        console.error("total clients error:", e);
      } finally {
        setTotalClientsLoading(false);
      }
    };
    fetchTotalClients();
  }, [totalClientsFilter]);

  useEffect(() => {
    const fetchNewSubs = async () => {
      setNewSubsLoading(true);
      try {
        const res = await axiosConfig({
          method: "get",
          url: newSubsFilter === "all" ? `investors/dashboard/new-subscribers-today/` : `investors/dashboard/new-subscribers-today/?filter_type=${newSubsFilter}`,
        });
        setNewSubsCount(res.data?.value ?? null);
      } catch (e) {
        console.error("new subscribers error:", e);
      } finally {
        setNewSubsLoading(false);
      }
    };
    fetchNewSubs();
  }, [newSubsFilter]);

  useEffect(() => {
    const fetchActiveUsersYest = async () => {
      setActiveUsersYestLoading(true);
      try {
        const res = await axiosConfig({
          method: "get",
          url: activeUsersYestFilter === "all" ? `investors/dashboard/active-users-yesterday/` : `investors/dashboard/active-users-yesterday/?filter_type=${activeUsersYestFilter}`,
        });
        setActiveUsersYestCount(res.data?.value ?? null);
      } catch (e) {
        console.error("active users yesterday filter error:", e);
      } finally {
        setActiveUsersYestLoading(false);
      }
    };
    fetchActiveUsersYest();
  }, [activeUsersYestFilter]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        const res = await axiosConfig({
          method: "get",
          url: "/investors/dashboard",
        });
        const data = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data || [];
        const mapped = data.map((item) => ({
          label: item.label,
          value:
            typeof item.value === "number"
              ? item.value.toLocaleString("en-IN")
              : item.value,
          change: item.change,
          icon: STAT_META[item.label]?.icon ?? (
            <GroupIcon sx={{ color: "#fff", fontSize: 22 }} />
          ),
          iconBg: STAT_META[item.label]?.iconBg ?? "#64748b",
          route: STAT_META[item.label]?.route ?? null,
        }));
        setStatCards(mapped);
      } catch (e) {
        console.error("investor dashboard stats error:", e);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, []);

  const handleLogOut = useCallback(async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      const user_id = userData?.response?.id || userData?.userDetails?.id;
      if (user_id) {
        await axiosConfig({
          method: "post",
          url: "/enroll/user-logout",
          data: { user_id, device_id: "web-client" },
        });
      }
    } finally {
      dispatch(logout());
      localStorage.clear();
      navigate("/login");
    }
  }, [dispatch, navigate]);

  return (
    <Box sx={{ background: "#f1f5f9", minHeight: "100vh" }}>
      {/* ── Page Header ── */}
      <Box
        sx={{
          background: "#ffffff",
          borderBottom: "1px solid #e2e8f0",
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Left — title */}
        <Box>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 800,
              color: "#0f172a",
              lineHeight: 1.2,
            }}
          >
            Overview Dashboard
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#94a3b8", mt: 0.3 }}>
            Complete overview of clients and business performance
          </Typography>
        </Box>

        {/* Right — user avatar + logout (only for investor) */}
        {userDetails?.user_type?.toLowerCase() === "investor" && (
          <>
            <Box
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.2,
                cursor: "pointer",
                "&:hover": { opacity: 0.85 },
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "#D1FAE5",
                  width: 38,
                  height: 38,
                  border: "2px solid #016551",
                }}
                src={RandomUser}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  lineHeight: 1.2,
                }}
              >
                <Typography
                  sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}
                >
                  {userDetails?.username || "User"}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                  {userDetails?.user_type || "Investor"}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: "#94a3b8", ml: 0.5 }}>
                ▾
              </Typography>
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              sx={{ mt: "8px" }}
              PaperProps={{
                sx: {
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  minWidth: 160,
                  overflow: "hidden",
                },
              }}
            >
              <MenuItem
                onClick={handleLogOut}
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#DC2626",
                  py: 1.2,
                  "&:hover": { background: "#fef2f2" },
                }}
              >
                Logout
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
      {/* ── Content ── */}
      <Box sx={{ p: 3 }}>
        {/* ── Stat Cards ── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {statsLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <Grid item xs={12} sm={6} md key={i}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: "14px",
                      border: "1px solid #e2e8f0",
                      background: "#fff",
                      p: 2.5,
                      height: "100%",
                      minHeight: 110,
                    }}
                  >
                    <Box
                      sx={{
                        background: "#f1f5f9",
                        borderRadius: 2,
                        height: 16,
                        width: "60%",
                        mb: 1.5,
                      }}
                    />
                    <Box
                      sx={{
                        background: "#f1f5f9",
                        borderRadius: 2,
                        height: 28,
                        width: "40%",
                        mb: 1,
                      }}
                    />
                    <Box
                      sx={{
                        background: "#f1f5f9",
                        borderRadius: 2,
                        height: 12,
                        width: "50%",
                      }}
                    />
                  </Paper>
                </Grid>
              ))
            : statCards.map((card, i) => {
                const isTotalClients = card.label === "Total Clients";
                const isNewSubs = card.label === "New Subscribers";
                const isActiveUsersYest = card.label === "Active Users";
                const isFiltered = isTotalClients || isNewSubs || isActiveUsersYest;
                const filteredValue = isTotalClients ? totalClientsCount : isNewSubs ? newSubsCount : activeUsersYestCount;
                const filteredLoading = isTotalClients ? totalClientsLoading : isNewSubs ? newSubsLoading : activeUsersYestLoading;
                const filteredFilter = isTotalClients ? totalClientsFilter : isNewSubs ? newSubsFilter : activeUsersYestFilter;
                const setFilteredFilter = isTotalClients ? setTotalClientsFilter : isNewSubs ? setNewSubsFilter : setActiveUsersYestFilter;
                return (
                <Grid item xs={12} sm={6} md key={i}>
                  <Paper
                    elevation={0}
                    onClick={() => !isFiltered && card.route && navigate(card.route)}
                    sx={{
                      borderRadius: "14px",
                      border: "1px solid #e2e8f0",
                      borderTop: `3px solid ${card.iconBg}`,
                      background: "#fff",
                      p: 2.5,
                      boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                      height: "100%",
                      position: "relative",
                      cursor: !isFiltered && card.route ? "pointer" : "default",
                      transition: "box-shadow 0.15s, transform 0.15s",
                      "&:hover": !isFiltered && card.route
                        ? {
                            boxShadow: "0 6px 20px rgba(0,0,0,0.10)",
                            transform: "translateY(-2px)",
                            "& .arrow-icon": { color: card.iconBg },
                          }
                        : {},
                    }}
                  >
                    {/* top-right: filter dropdown for filtered cards, else arrow */}
                    {isFiltered ? (
                      <Select
                        size="small"
                        value={filteredFilter}
                        onChange={(e) => setFilteredFilter(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          fontSize: 11,
                          height: 28,
                          borderRadius: "7px",
                          color: "#475569",
                          minWidth: 120,
                          ".MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                          ".MuiSelect-select": { py: "4px", fontSize: 11 },
                        }}
                      >
                        {!isNewSubs && <MenuItem value="all">All</MenuItem>}
                        <MenuItem value="today">Today</MenuItem>
                        <MenuItem value="yesterday">Yesterday</MenuItem>
                        <MenuItem value="last_7_days">Last 7 Days</MenuItem>
                        <MenuItem value="last_15_days">Last 15 Days</MenuItem>
                        <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                        <MenuItem value="last_90_days">Last 90 Days</MenuItem>
                        <MenuItem value="last_1_year">Last 1 Year</MenuItem>
                      </Select>
                    ) : (
                      <Box
                        className="arrow-circle"
                        sx={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <ChevronRightIcon
                          className="arrow-icon"
                          sx={{
                            fontSize: 16,
                            color: "#94a3b8",
                            transition: "color 0.15s",
                            transform: "rotate(-45deg)",
                          }}
                        />
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 1.2,
                        mt: 3.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "10px",
                          background: card.iconBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {card.icon}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: "#64748b",
                          fontWeight: 600,
                        }}
                      >
                        {card.label}
                      </Typography>
                    </Box>

                    {isFiltered ? (
                      filteredLoading ? (
                        <Box sx={{ height: 28, width: 80, background: "#f1f5f9", borderRadius: 2 }} />
                      ) : (
                        <Typography sx={{ fontSize: 28, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>
                          {filteredValue != null
                            ? Number(filteredValue).toLocaleString("en-IN")
                            : card.value}
                        </Typography>
                      )
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 28,
                          fontWeight: 800,
                          color: "#0f172a",
                          lineHeight: 1,
                        }}
                      >
                        {card.value}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
                );
              })}
        </Grid>

        {/* ── Period Filter Cards Row ── */}
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
          {/* Total Users Acquired */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "14px",
                border: "1px solid #e2e8f0",
                borderTop: "3px solid #016551",
                background: "#fff",
                p: 2.5,
                height: "100%",
                boxSizing: "border-box",
                boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "10px",
                    background: "#016551",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PersonAddIcon sx={{ color: "#fff", fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
                    {userBeforeData?.label || "Subscribed Active Users"}
                  </Typography>
                  {userBeforeLoading ? (
                    <Box sx={{ height: 26, width: 70, background: "#f1f5f9", borderRadius: 2, mt: 0.5 }} />
                  ) : (
                    <Typography sx={{ fontSize: 26, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>
                      {userBeforeData?.value != null
                        ? Number(userBeforeData.value).toLocaleString("en-IN")
                        : "—"}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Select
                size="small"
                value={userBeforeFilter}
                onChange={(e) => setUserBeforeFilter(e.target.value)}
                sx={{
                  fontSize: 12,
                  height: 32,
                  borderRadius: "8px",
                  color: "#475569",
                  minWidth: 130,
                  ".MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="last_7_days">Last 7 Days</MenuItem>
                <MenuItem value="last_15_days">Last 15 Days</MenuItem>
                <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                <MenuItem value="last_90_days">Last 90 Days</MenuItem>
                <MenuItem value="last_1_year">Last 1 Year</MenuItem>
              </Select>
            </Paper>
          </Grid>

          {/* Active Subscribers Before Days */}
          {/* <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "14px",
                border: "1px solid #e2e8f0",
                borderTop: "3px solid #0EA5E9",
                background: "#fff",
                p: 2.5,
                height: "100%",
                boxSizing: "border-box",
                boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "10px",
                    background: "#0EA5E9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <SubscriptionsIcon sx={{ color: "#fff", fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
                    Active Subscribers
                  </Typography>
                  {activeSubBeforeLoading ? (
                    <Box sx={{ height: 26, width: 70, background: "#f1f5f9", borderRadius: 2, mt: 0.5 }} />
                  ) : (
                    <Typography sx={{ fontSize: 26, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>
                      {activeSubBeforeData?.total_active_users != null
                        ? Number(activeSubBeforeData.total_active_users).toLocaleString("en-IN")
                        : "—"}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Select
                size="small"
                value={activeSubBeforeFilter}
                onChange={(e) => setActiveSubBeforeFilter(e.target.value)}
                sx={{
                  fontSize: 12,
                  height: 32,
                  borderRadius: "8px",
                  color: "#475569",
                  minWidth: 130,
                  ".MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="last_7_days">Last 7 Days</MenuItem>
                <MenuItem value="last_15_days">Last 15 Days</MenuItem>
                <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                <MenuItem value="last_90_days">Last 90 Days</MenuItem>
              </Select>
            </Paper>
          </Grid> */}

          {/* Subscription Payment Data */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "14px",
                border: "1px solid #e2e8f0",
                borderTop: "3px solid #F59E0B",
                background: "#fff",
                p: 2.5,
                height: "100%",
                boxSizing: "border-box",
                boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "10px",
                    background: "#F59E0B",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <AccountBalanceWalletIcon sx={{ color: "#fff", fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
                    Subscription Revenue
                  </Typography>
                  {subPaymentLoading ? (
                    <Box sx={{ height: 26, width: 70, background: "#f1f5f9", borderRadius: 2, mt: 0.5 }} />
                  ) : (
                    <>
                      <Typography sx={{ fontSize: 26, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>
                        {subPaymentData?.total_payment_amount != null
                          ? `₹${Number(subPaymentData.total_payment_amount).toLocaleString("en-IN")}`
                          : "—"}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1.5, mt: 0.5 }}>
                        <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                          Payments:{" "}
                          <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
                            {subPaymentData?.total_payment_count?.toLocaleString("en-IN") ?? "—"}
                          </Box>
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                          Users:{" "}
                          <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
                            {subPaymentData?.total_users?.toLocaleString("en-IN") ?? "—"}
                          </Box>
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
              <Select
                size="small"
                value={subPaymentFilter}
                onChange={(e) => setSubPaymentFilter(e.target.value)}
                sx={{
                  fontSize: 12,
                  height: 32,
                  borderRadius: "8px",
                  color: "#475569",
                  minWidth: 130,
                  ".MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="last_7_days">Last 7 Days</MenuItem>
                <MenuItem value="last_15_days">Last 15 Days</MenuItem>
                <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                <MenuItem value="last_90_days">Last 90 Days</MenuItem>
                <MenuItem value="last_1_year">Last 1 Year</MenuItem>
              </Select>
            </Paper>
          </Grid>
        </Grid>

        {/* ── Charts Row ── */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Logins & Subscribers Trend */}
          <Grid item xs={12} md={4.5}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "14px",
                border: "1px solid #e2e8f0",
                background: "#fff",
                p: 2.5,
                boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}
                >
                  Logins &amp; Subscribers Trend
                </Typography>
                {/* <Typography sx={{ fontSize: 12, color: "#016551", fontWeight: 600, cursor: "pointer" }}>View All</Typography> */}
              </Box>
              <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: "#0EA5E9",
                    }}
                  />
                  <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                    Logins
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: "#016551",
                    }}
                  />
                  <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                    Subscribes
                  </Typography>
                </Box>
              </Box>
              {trendLoading ? (
                <Box
                  sx={{ height: 200, background: "#f1f5f9", borderRadius: 2 }}
                />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart
                    data={trendData}
                    margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#f1f5f9"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: "#94a3b8" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="left"
                      tick={{ fontSize: 10, fill: "#0EA5E9" }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) =>
                        v >= 1000 ? `${(v / 1000).toFixed(1)}K` : v
                      }
                      width={36}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 10, fill: "#016551" }}
                      axisLine={false}
                      tickLine={false}
                      width={28}
                    />
                    <Tooltip
                      contentStyle={{
                        fontSize: 12,
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                      }}
                      formatter={(value, name) => [
                        value.toLocaleString("en-IN"),
                        name === "logins" ? "Logins" : "Subscribers",
                      ]}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="logins"
                      stroke="#0EA5E9"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: "#0EA5E9" }}
                      activeDot={{ r: 5 }}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="subscribes"
                      stroke="#016551"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: "#016551" }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          {/* New Clients Trend */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "14px",
                border: "1px solid #e2e8f0",
                background: "#fff",
                p: 2.5,
                boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}
                >
                  New Clients Registered
                </Typography>
                {/* <Typography sx={{ fontSize: 12, color: "#016551", fontWeight: 600, cursor: "pointer" }}>View All</Typography> */}
              </Box>
              {newClientLoading ? (
                <Box
                  sx={{ height: 200, background: "#f1f5f9", borderRadius: 2 }}
                />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={newClientData} barSize={28}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#f1f5f9"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: "#94a3b8" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "#94a3b8" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        fontSize: 12,
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                      }}
                      formatter={(value) => [
                        value.toLocaleString("en-IN"),
                        "New Clients",
                      ]}
                    />
                    <Bar
                      dataKey="clients"
                      fill="#016551"
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          {/* Subscription Rate */}
          <Grid item xs={12} md={3.5}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "14px",
                border: "1px solid #e2e8f0",
                background: "#fff",
                p: 2.5,
                boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography
                  sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}
                >
                  Subscription Rate
                </Typography>
                <Select
                  size="small"
                  value={subRateFilter}
                  onChange={(e) => setSubRateFilter(e.target.value)}
                  sx={{
                    fontSize: 12,
                    height: 30,
                    borderRadius: "8px",
                    color: "#475569",
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e2e8f0",
                    },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="last_7_days">Last 7 Days</MenuItem>
                </Select>
              </Box>
              {subRateLoading ? (
                <Box
                  sx={{ height: 220, background: "#f1f5f9", borderRadius: 2 }}
                />
              ) : (
                <>
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <PieChart width={160} height={160}>
                      <Pie
                        data={[
                          {
                            name: "Subscribed",
                            value: subRateData?.conversion_rate ?? 0,
                          },
                          {
                            name: "Non-subscribed",
                            value: 100 - (subRateData?.conversion_rate ?? 0),
                          },
                        ]}
                        cx={75}
                        cy={75}
                        innerRadius={52}
                        outerRadius={72}
                        startAngle={90}
                        endAngle={-270}
                        dataKey="value"
                        strokeWidth={0}
                      >
                        <Cell fill="#016551" />
                        <Cell fill="#e2e8f0" />
                      </Pie>
                    </PieChart>
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%,-50%)",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 17,
                          fontWeight: 800,
                          color: "#016551",
                          lineHeight: 1,
                        }}
                      >
                        {subRateData?.conversion_rate?.toFixed(2) ?? "0.00"}%
                      </Typography>
                      <Typography sx={{ fontSize: 9, color: "#94a3b8" }}>
                        Conversion Rate
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.8,
                    }}
                  >
                    {[
                      {
                        label: "Total Visitors",
                        value:
                          subRateData?.total_visitors?.toLocaleString(
                            "en-IN",
                          ) ?? "—",
                      },
                      {
                        label: "Total Subscriptions",
                        value:
                          subRateData?.total_subscriptions?.toLocaleString(
                            "en-IN",
                          ) ?? "—",
                      },
                      {
                        label: "Conversion Rate",
                        value: `${subRateData?.conversion_rate?.toFixed(2) ?? "0.00"}%`,
                      },
                    ].map((item) => (
                      <Box
                        key={item.label}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                          {item.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#0f172a",
                          }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* ── Bottom Row ── */}
        <Grid container spacing={2}>
          {/* Active Subscribers Table */}
          <Grid item xs={12} md={8.5}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "14px",
                border: "1px solid #e2e8f0",
                background: "#fff",
                boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                <Box>
                  <Typography
                    sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}
                  >
                    Active Subscribers
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                    as of{" "}
                    {new Date().toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </Typography>
                </Box>
                <Typography
                  onClick={() => navigate("/investor/active-subscribers")}
                  sx={{
                    fontSize: 12,
                    color: "#016551",
                    fontWeight: 600,
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  View All
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: "#f8fafc" }}>
                      {[
                        "#",
                        "Name",
                        "Phone Number",
                        "Email",
                        "Join Date",
                        "Status",
                      ].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#64748b",
                            py: 1.2,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subscribersLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 6 }).map((__, j) => (
                            <TableCell key={j}>
                              <Box
                                sx={{
                                  height: 12,
                                  background: "#f1f5f9",
                                  borderRadius: 1,
                                  width: j === 0 ? 20 : "80%",
                                }}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : activeSubscribers.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          sx={{
                            textAlign: "center",
                            py: 3,
                            fontSize: 13,
                            color: "#94a3b8",
                          }}
                        >
                          No new subscribers today
                        </TableCell>
                      </TableRow>
                    ) : (
                      activeSubscribers.map((row, i) => (
                        <TableRow
                          key={i}
                          sx={{ "&:hover": { background: "#f8fafc" } }}
                        >
                          <TableCell sx={{ fontSize: 12, color: "#64748b" }}>
                            {i + 1}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#0f172a",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.userid__username}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: 12,
                              color: "#475569",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.userid__phoneno}
                          </TableCell>
                          <TableCell sx={{ fontSize: 11, color: "#64748b" }}>
                            {row.userid__email}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: 11,
                              color: "#64748b",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.created_on}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label="Active"
                              size="small"
                              sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                background: "#dcfce7",
                                color: "#16a34a",
                                height: 20,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Downloads Overview — hidden for now */}
          {/* <Grid item xs={12} md={3.5}>
          <Paper elevation={0} sx={{ borderRadius: "14px", border: "1px solid #e2e8f0", background: "#fff", p: 2.5, boxShadow: "0 1px 6px rgba(0,0,0,0.06)", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Downloads Overview</Typography>
              <PeriodSelect value={downloadPeriod} onChange={setDownloadPeriod} />
            </Box>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {[{ label: "Total Downloads", value: "45,680" }, { label: "Total Downloads", value: "45,680" }].map((item, i) => (
                <Grid item xs={6} key={i}>
                  <Box sx={{ background: "#f8fafc", borderRadius: "10px", p: 1.5, border: "1px solid #e2e8f0" }}>
                    <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>{item.label}</Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a", mt: 0.3 }}>{item.value}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Download to Login Rate</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>45,680</Typography>
                <Typography sx={{ fontSize: 12, color: "#22c55e", fontWeight: 600 }}>12.5% on Yesterday</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={58}
                sx={{ height: 8, borderRadius: 4, bgcolor: "#f1f5f9", "& .MuiLinearProgress-bar": { bgcolor: "#016551", borderRadius: 4 } }}
              />
              <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>58% out of 100%</Typography>
            </Box>
          </Paper>
        </Grid> */}

          {/* Digital Spend */}
          <Grid item xs={12} md={3.5}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "14px",
                border: "1px solid #e2e8f0",
                background: "#fff",
                p: 2.5,
                boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Typography
                  sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}
                >
                  Digital Spend
                </Typography>
                <Typography
                  onClick={() => navigate("/investor/marketing-spend")}
                  sx={{
                    fontSize: 12,
                    color: "#016551",
                    fontWeight: 600,
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  View All
                </Typography>
                <Select
                  size="small"
                  value={spendPeriod}
                  onChange={(e) => setSpendPeriod(e.target.value)}
                  sx={{
                    fontSize: 12,
                    height: 30,
                    borderRadius: "8px",
                    color: "#475569",
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e2e8f0",
                    },
                  }}
                >
                  {[
                    ["7", "Last 7 Days"],
                    ["15", "Last 15 Days"],
                    ["30", "Last 30 Days"],
                    ["90", "Last 90 Days"],
                  ].map(([v, label]) => (
                    <MenuItem key={v} value={v}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                  Total Spend
                </Typography>
              </Box>
              <Typography
                sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a", mb: 1 }}
              >
                {spendLoading ? "—" : totalSpendDisplay}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                <PieChart key={spendPeriod} width={130} height={130}>
                  <Pie
                    data={
                      spendChartData.length
                        ? spendChartData
                        : [{ name: "No data", value: 1 }]
                    }
                    cx={60}
                    cy={60}
                    innerRadius={0}
                    outerRadius={60}
                    dataKey="value"
                    strokeWidth={1}
                    stroke="#fff"
                    minAngle={8}
                  >
                    {(spendChartData.length ? spendChartData : [{}]).map(
                      (_, i) => (
                        <Cell
                          key={i}
                          fill={
                            spendChartData.length
                              ? SPEND_COLORS[i % SPEND_COLORS.length]
                              : "#e2e8f0"
                          }
                        />
                      ),
                    )}
                  </Pie>
                </PieChart>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                {spendChartData.map((item, i) => (
                  <Box
                    key={item.name}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
                    >
                      <Box
                        sx={{
                          width: 9,
                          height: 9,
                          borderRadius: "50%",
                          background: SPEND_COLORS[i % SPEND_COLORS.length],
                          flexShrink: 0,
                        }}
                      />
                      <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                        {item.name}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{ fontSize: 11, fontWeight: 700, color: "#0f172a" }}
                    >
                      ₹{Number(item.value).toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>{" "}
      {/* end content p:3 */}
    </Box>
  );
};

export default InvestorDashboard;
